import axios, { AxiosError, AxiosInstance } from 'axios';

/**
 * Base URL for API requests
 */
const API_URL = import.meta.env.VITE_API_URL;

/**
 * Flag to prevent multiple simultaneous auth error handling
 */
let isHandlingAuth = false;

/**
 * Handles authentication errors by clearing local storage and redirecting to login
 */
const handleAuthError = async () => {
  if (isHandlingAuth) return;
  isHandlingAuth = true;
  
  try {
    localStorage.removeItem('token');
    window.location.href = '/auth';
  } catch (error) {
    console.error('Error during forced logout:', error);
  } finally {
    isHandlingAuth = false;
  }
};

/**
 * Protected axios instance with auth error handling.
 * Use this for endpoints that require authentication.
 * Will handle 401 errors by logging out the user.
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
});

/**
 * Public axios instance without auth error handling.
 * Use this for:
 * 1. Public endpoints that don't require authentication
 * 2. Endpoints that can work with or without auth
 */
export const publicAxiosInstance: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
});

// Add response interceptor only to protected instance
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      await handleAuthError();
    }
    return Promise.reject(error);
  }
);

// Add request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default axiosInstance;
